import {
  ILinkIconFragmentFragment,
  IPushFragmentFragment,
  IRegionWebFragmentFragment,
} from '~Api/Graphql/gql/types.generated';
import useMember from './useMember';
import {findRegionsByDocumentUrl, useRegions} from './useRegions';
import {RouteTypename} from '~Navigation/type';
import store from '..';
import {useContext, useEffect, useMemo, useState} from 'react';
import {AuthContext} from '@src/Providers/AuthProvider/AuthProvider';
import {SupportedLanguages, SUPPORTED_LANGUAGES} from '@src/Constant/languages';
import useChangeLanguage from '@src/Hooks/useChangeLanguage';
import {languageMapper} from '@src/Services/i18n/languageMapper';
import { UmbTypeChecker } from '@src/Umbraco/Utils/UmbTypeChecker';

export interface IPageData {
  id: string;
  __typename: RouteTypename;
  url?: string;
}

interface IRegionSubPage {
  home?: IPageData;
  watch?: IPageData & {
    children: {
      vod?: IPageData;
      liveTv?: IPageData;
      liveEvent?: IPageData;
    };
  };
  listen?: IPageData & {
    children: {
      radio?: IPageData;
      podcast?: IPageData;
    };
  };
  learn?: IPageData;
  artist?: IPageData;
  searchTopBlocks?: ILinkIconFragmentFragment[];
  searchPageBlocks?: any;
  push?: IPushFragmentFragment;
}

interface RegionUrlReturn {
  regionUrl: string | undefined;
  currentRegion?: IRegionWebFragmentFragment;
  page: IRegionSubPage;
  loading: boolean;
}

//TODO: DRY the code
export const getActiveRegionPage = (
  forcedRegion?: IRegionWebFragmentFragment,
): IRegionSubPage => {
  const {
    member: {member},
    region: {regions},
  } = store.getState();
  let activeRegion: IRegionWebFragmentFragment | undefined = forcedRegion;

  if (!activeRegion) {
    activeRegion = member?.region
      ? findRegionsByDocumentUrl(member.region, regions)
      : undefined;
  }

  const currentRegion = activeRegion || regions[0];

  const page: IRegionSubPage = {};

  const isRegion = UmbTypeChecker.isType(currentRegion, 'Region');
  if (isRegion) {
    const {
      homepageUrl,
      webArtistHome,
      watchHomepage,
      listenHomepage,
      learnHomepage,
      searchTopBlocks,
      searchPageBlocks,
      push,
    } = currentRegion;

    const pagePush = UmbTypeChecker.asType(push?.[0]?.content, 'Push');
    if (pagePush) {
      page.push = pagePush;
    }

    if (homepageUrl) {
      const {id, __typename, url} = homepageUrl;
      page.home = {
        id,
        __typename,
        url: url ?? '',
      };
    }

    if (webArtistHome) {
      const {id, __typename, url} = webArtistHome;
      page.artist = {
        id,
        __typename,
        url: url ?? '',
      };
    }

    const watchItems = watchHomepage?.children?.items;
    if (watchItems) {
      const children: NonNullable<IRegionSubPage['watch']>['children'] = {};
      watchItems.forEach(item => {
        if (item) {
          if (item.__typename === 'SectionPage') {
            children.vod = {
              id: item.id,
              __typename: item.__typename,
              url: item.url,
            };
          } else if (item.__typename === 'LiveTVPage') {
            children.liveTv = {
              id: item.id,
              __typename: item.__typename,
              url: item.url,
            };
          } else if (item.__typename === 'LiveEventsPage') {
            children.liveEvent = {
              id: item.id,
              __typename: item.__typename,
              url: item.url,
            };
          }
        }
      });

      const {id, __typename, url} = watchHomepage;
      page.watch = {
        id,
        __typename,
        url,
        children,
      };
    }

    const listenItems = listenHomepage?.children?.items;
    if (listenItems) {
      const children: Record<'radio' | 'podcast', IPageData | undefined> = {
        radio: undefined,
        podcast: undefined,
      };

      listenItems.forEach(item => {
        if (item) {
          const isRadio = (item.url ?? '').includes('radio');
          const key = isRadio ? 'radio' : 'podcast';

          const {id, __typename, url} = item;
          children[key] = {
            id,
            __typename,
            url,
          };
        }
      });

      const {id, __typename, url} = listenHomepage;
      page.listen = {
        id,
        __typename,
        url,
        children,
      };
    }

    if (learnHomepage) {
      page.learn = {
        id: learnHomepage.id,
        __typename: learnHomepage.__typename,
        url: learnHomepage.url,
      };
    }

    if (searchTopBlocks && searchTopBlocks.length > 0) {
      let data: ILinkIconFragmentFragment[] = [];
      searchTopBlocks.forEach(item => {
        if (item && item.content.__typename === 'LinkIcon') {
          let value = item.content as ILinkIconFragmentFragment;
          data.push(value);
        }
      });
      page.searchTopBlocks = data;
    }

    if (searchPageBlocks) {
      page.searchPageBlocks = searchPageBlocks;
    }
  }

  return page;
};

const getCurrentRegionByLanguage = (
  langIsoCode: string,
  regions: IRegionWebFragmentFragment[],
) => {
  const lang = languageMapper(langIsoCode);
  if (!SUPPORTED_LANGUAGES.includes(lang as SupportedLanguages)) return;

  const currentRegion = regions.find(
    region => region?.defaultLanguage?.toLowerCase() === lang.toLowerCase(),
  );
  return currentRegion;
};

export const useRegionUrl = (): RegionUrlReturn => {
  const {isLoggedIn} = useContext(AuthContext);
  const {currentLanguage} = useChangeLanguage();
  const member = useMember();
  const {regions} = useRegions();
  const [currentRegion, setCurrentRegion] =
    useState<IRegionWebFragmentFragment>(regions[0]);

  const activeRegion = useMemo(() => {
    if (isLoggedIn) {
      if (member?.region) {
        return findRegionsByDocumentUrl(member.region, regions);
      }
      return undefined;
    } else {
      return getCurrentRegionByLanguage(currentLanguage, regions);
    }
  }, [isLoggedIn, currentLanguage, member?.region, regions, currentLanguage]);

  useEffect(
    () => activeRegion && setCurrentRegion(activeRegion),
    [activeRegion],
  );


  /** Handles anonymous content lang switch */
  const page: IRegionSubPage = useMemo(
    () => getActiveRegionPage(!isLoggedIn ? currentRegion : undefined),
    [currentRegion],
  );

  return {
    regionUrl: currentRegion?.url,
    currentRegion,
    page,
    loading: !(page && currentRegion),
  };
};
